import React, { useEffect, useState } from "react";
import type { SwHandle } from "./platform/browser";
import { install, isIOS, prefs } from "./platform/browser";
import { isNative } from "./platform/native";
import { parseAssemblyLink, parseDdayLink, parseDriveLink } from "./field/link";
import { useApp, useQuery, useSession, useSyncStatus } from "./ui/context";
import { href, navigate, useRoute, type Route } from "./ui/router";
import { Banner, Button } from "./ui/components";
import { AllPlacesScreen } from "./screens/AllPlaces";
import { AssemblyPick } from "./screens/AssemblyPick";
import { BoothScreen } from "./screens/Booth";
import { BoothsScreen } from "./screens/Booths";
import { CampaignPart } from "./screens/Campaign";
import { DriveScreen } from "./screens/Drive";
import { PeopleScreen } from "./screens/People";
import { ResultScreen } from "./screens/Result";
import { PlacesScreen } from "./screens/Places";
import { ExitPollScreen } from "./screens/ExitPoll";
import { SentimentScreen } from "./screens/Sentiment";
import { LocationScreen } from "./screens/Location";
import { LoginScreen } from "./screens/Login";
import { PollingScreen } from "./screens/Polling";
import { SectionScreen } from "./screens/Section";
import { SyncScreen } from "./screens/Sync";
import { VotedScreen } from "./screens/Voted";
import { CountingScreen } from "./screens/Counting";
import { SurveyScreen } from "./screens/Survey";
import { AttendanceScreen } from "./screens/Attendance";
import { ReviewScreen } from "./screens/Review";
import { HomeScreen } from "./screens/Home";

export function App({ sw, storageProblem }: { sw: SwHandle; storageProblem: string | null }) {
  const { app, t, lang, toggleLang } = useApp();
  const { token, user, assemblyId } = useSession();
  const status = useSyncStatus();
  const route = useRoute();
  const [update, setUpdate] = useState(false);
  useEffect(() => sw.onUpdate(() => setUpdate(true)), [sw]);
  // duties given in the admin panel reach this login without logging in again
  useEffect(() => {
    void app.refreshMe(true);
    const again = () => document.visibilityState === "visible" && void app.refreshMe();
    document.addEventListener("visibilitychange", again);
    window.addEventListener("online", again);
    return () => {
      document.removeEventListener("visibilitychange", again);
      window.removeEventListener("online", again);
    };
  }, [app, token]);
  // many bottom buttons scroll sideways: keep the open one in view
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('.tabbar [aria-current="page"]');
    el?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  }, [route]);
  useEffect(() => {
    void followLink(app);
  }, [app]);
  // the assembly list is cached in the background after login; reload when it arrives ("kv")
  const assemblies = useQuery(() => app.assemblies(), [token], ["kv"]);
  const a = assemblies?.find((x) => x.id === assemblyId);
  const assemblyName = assemblyId ? (a ? (lang === "hi" && a.nameHi) || a.name : assemblyId) : null;

  const loggedIn = !!token && !!user;
  // polling-day worker: only the polling screen (and sync) for their own booths
  const worker = user?.role === "worker";
  // a vehicle's driver: only the duty screen
  const driver = user?.role === "driver";
  // the candidate's viewer login (N-PAC, 4 Nov 2026: "Review all work"): the dashboard inside the app, read-only
  const viewer = user?.role === "viewer";
  const needsAssembly = loggedIn && !assemblyId;

  // what this login's duties open (N-PAC, 31 Oct 2026: "एडमिन पैनल से जिसको जो काम आवंटित किया जायेगा उसके
  // आइडी पासवर्ड से खुलेगा"): the home screen shows one icon per section (N-PAC, 4 Nov 2026: "दस आइकन");
  // the bottom bar keeps only होम, मतदान दिवस (SOS) and सिंक
  const screens = loggedIn ? app.screens : [];
  const hasPlaces = screens.includes("places");
  const hasBoothWork = screens.some((x) => x !== "places" && x !== "rallies" && x !== "tour" && x !== "vehicles" && x !== "exit_poll" && x !== "voted" && x !== "counting" && x !== "survey" && x !== "attendance");

  let screen: React.ReactNode;
  if (!loggedIn) screen = <LoginScreen />;
  else if (driver) screen = <DriveScreen />;
  else if (worker) screen = route.name === "sync" ? <SyncScreen /> : <PollingScreen />;
  else if (needsAssembly || route.name === "assembly") screen = <AssemblyPick />;
  else if (viewer) screen = route.name === "sync" ? <SyncScreen /> : <ReviewScreen />;
  else screen = <Screen route={route.name === "campaign" ? { name: "home" } : route.name === "booths" && !hasBoothWork ? { name: "home" } : route} />;

  const bad = status.counts.failed + status.counts.conflict > 0;
  const tab: "home" | "polling" | "sync" | "review" =
    route.name === "polling" || (worker && route.name !== "sync") ? "polling" : route.name === "sync" ? "sync" : viewer ? "review" : "home";
  // screens with no back button: the roots of the bottom bar
  const top = viewer ? ["home", "sync", "review"] : ["home", "polling", "sync", "campaign"];

  return (
    <div className="app">
      <header className="topbar">
        {loggedIn && !worker && !driver && !top.includes(route.name) ? (
          <button className="hbtn" aria-label={t.back} onClick={() => navigate(route.name === "places" && hasPlaces && !hasBoothWork ? { name: "allplaces" } : parentOf(route))} data-testid="back">
            ←
          </button>
        ) : null}
        <img className="brand-mark" src="/brand/npac-mark.webp" alt="" width="34" height="34" />
        <h1>
          {t.appName}
          {loggedIn && assemblyName ? (
            <span className="sub" data-testid="assembly-name">
              {assemblyName}
              {user?.name ? ` • ${user.name}` : ""}
            </span>
          ) : null}
        </h1>
        <button className="hbtn" onClick={toggleLang} data-testid="lang-toggle">
          {t.langToggle}
        </button>
      </header>
      {status.online === false ? (
        <div className="offline-bar" role="status" data-testid="offline-bar">
          ⚡ {t.offlineBar}
        </div>
      ) : null}

      <main>
        {storageProblem ? <Banner kind="error">{storageProblem}</Banner> : null}
        {update ? (
          <Banner kind="info" action={{ label: t.updateNow, onClick: () => sw.applyUpdate() }}>
            {t.updateReady}
          </Banner>
        ) : null}
        {loggedIn && app.linkMismatch && assemblyId ? (
          <Banner kind="warn" action={{ label: t.logoutOther, onClick: () => void app.logout() }}>
            {t.linkMismatch(app.linkMismatch, assemblyId)}
          </Banner>
        ) : null}
        {screen}
      </main>

      {loggedIn && !needsAssembly && !driver ? (
        <nav className="tabbar" aria-label="tabs">
          {viewer ? (
            <a className="tab" href={href({ name: "review" })} aria-current={tab === "review" ? "page" : undefined} data-testid="tab-review">
              <span className="ico" aria-hidden>
                👁
              </span>
              {t.tabReview}
            </a>
          ) : worker ? null : (
            <a className="tab" href={href({ name: "home" })} aria-current={tab === "home" ? "page" : undefined} data-testid="tab-home">
              <span className="ico" aria-hidden>
                🏠
              </span>
              {t.tabHome}
            </a>
          )}
          {viewer ? null : (
            <a className="tab tab-sos" href={href({ name: "polling" })} aria-current={tab === "polling" ? "page" : undefined} data-testid="tab-polling">
              <span className="ico" aria-hidden>
                🚨
              </span>
              {t.tabPolling}
            </a>
          )}
          <a className="tab" href={href({ name: "sync" })} aria-current={tab === "sync" ? "page" : undefined} data-testid="tab-sync">
            <span className="ico" aria-hidden>
              {status.running ? "⟳" : status.counts.total ? "⇅" : "✓"}
            </span>
            <span>
              {t.tabSync}{" "}
              {status.counts.total ? (
                <span className={`badge${bad ? " bad" : ""}`} data-testid="sync-badge">
                  {status.counts.total}
                </span>
              ) : null}
            </span>
          </a>
        </nav>
      ) : null}
      <InstallHint />
    </div>
  );
}

/**
 * The link the app was opened with decides where it lands, even when a login is already on this phone
 * (N-PAC, 28 Oct 2026: Chandni Chowk's link opened Panchkula):
 *  - polling-day link: a worker session of another link is ended, so the new link opens by itself;
 *    a surveyor / admin of that assembly goes to the polling-day tab.
 *  - assembly link: an admin who may open that assembly switches to it; otherwise the link is ignored (the
 *    login's own assembly stays); a worker session is ended so the surveyor login shows.
 */
async function followLink(app: ReturnType<typeof useApp>["app"]) {
  const link = parseAssemblyLink(location.search);
  if (!link || !app.user || !app.token) return;
  const dday = new URLSearchParams(location.search).get("dday") === "1" ? parseDdayLink(location.search) : null;
  const u = app.user;
  const drive = parseDriveLink(location.search);
  if (u.role === "driver") {
    // another vehicle's link (or any other link) ends this driver session; the login screen opens it
    if (!drive || (await app.driveLinkInUse()) !== `${link.assemblyId}|${drive}`) await app.logout();
    return;
  }
  if (drive) {
    // a driver link opened on a phone signed in as someone else: it is meant for the driver's phone
    await app.logout();
    return;
  }
  if (u.role === "worker") {
    const inUse = await app.ddayLinkInUse();
    if (!dday || inUse !== `${link.assemblyId}|${dday.key}`) await app.logout();
    return;
  }
  // the login decides the assembly (N-PAC, 4 Nov 2026): a link to an assembly this login cannot open is not used
  const mayOpen = u.role === "super_admin" || (u.assemblyIds ?? (u.assemblyId ? [u.assemblyId] : [])).includes(link.assemblyId);
  if (link.assemblyId !== app.assemblyId) {
    if (!mayOpen) return;
    app.linkMismatch = null;
    await app.chooseAssembly(link.assemblyId);
  }
  if (dday) navigate({ name: "polling" }, true);
}

function parentOf(r: Route): Route {
  if (r.name === "section" || r.name === "location" || r.name === "people" || r.name === "sentiment" || r.name === "result" || r.name === "places") return { name: "booth", code: r.code };
  if (r.name === "booth") return { name: "booths" };
  return { name: "home" };
}

function Screen({ route }: { route: Route }) {
  switch (route.name) {
    case "home":
      return <HomeScreen />;
    case "booth":
      return <BoothScreen key={route.code} code={route.code} />;
    case "section":
      return <SectionScreen key={`${route.code}/${route.section}`} code={route.code} section={route.section} />;
    case "location":
      return <LocationScreen key={route.code} code={route.code} />;
    case "people":
      return <PeopleScreen key={`${route.code}/${route.list}`} code={route.code} list={route.list} />;
    case "sentiment":
      return <SentimentScreen key={route.code} code={route.code} />;
    case "result":
      return <ResultScreen key={route.code} code={route.code} />;
    case "places":
      return <PlacesScreen key={route.code} code={route.code} />;
    case "allplaces":
      return <AllPlacesScreen />;
    case "camp":
      return <CampaignPart key={route.part} part={route.part} />;
    case "polling":
      return <PollingScreen />;
    case "exitpoll":
      return <ExitPollScreen />;
    case "voted":
      return <VotedScreen />;
    case "counting":
      return <CountingScreen />;
    case "survey":
      return <SurveyScreen />;
    case "attendance":
      return <AttendanceScreen />;
    case "review":
      return <ReviewScreen />;
    case "sync":
      return <SyncScreen />;
    default:
      return <BoothsScreen />;
  }
}

/**
 * First open in a browser tab (not installed): how to add the app to the home screen. Never in front
 * of the polling-day SOS screen: whoever opens that link must be able to raise an SOS at once.
 */
function InstallHint() {
  const { t } = useApp();
  const { user } = useSession();
  const pollingLink = typeof location !== "undefined" && new URLSearchParams(location.search).get("dday") === "1";
  // never inside the Android app (it is already installed)
  const [show, setShow] = useState(() => !isNative() && !install.isStandalone() && !prefs.get("installHintDone"));
  const [canPrompt, setCanPrompt] = useState(install.canPrompt());
  useEffect(() => {
    const off = install.subscribe(() => setCanPrompt(install.canPrompt()));
    return () => {
      off();
    };
  }, []);
  if (!show || pollingLink || user?.role === "worker") return null;
  const close = () => {
    prefs.set("installHintDone", "1");
    setShow(false);
  };
  const ios = isIOS();
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="install-title" data-testid="install-hint">
      <div className="sheet">
        <h2 id="install-title">📲 {t.installTitle}</h2>
        <p style={{ fontWeight: 700 }}>{t.installWhy}</p>
        <ol>
          {ios ? (
            <>
              <li>{t.installIphone}</li>
              <li>{t.installAndroid}</li>
            </>
          ) : (
            <>
              <li>{t.installAndroid}</li>
              <li>{t.installIphone}</li>
            </>
          )}
        </ol>
        <div className="stack">
          {canPrompt ? (
            <Button
              big
              onClick={async () => {
                if (await install.prompt()) close();
              }}
            >
              {t.installButton}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={close} testId="install-dismiss">
            {t.installDismiss}
          </Button>
        </div>
      </div>
    </div>
  );
}
